/**
 * Страница входа через Twitch.
 *
 * Коды ошибок приходят от серверного OAuth-колбэка в query. Каждый
 * переводится в понятное пользователю объяснение: «что-то пошло не так»
 * не помогает понять, нужно ли повторить попытку или проблема в настройке.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ShieldCheck } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { Logo } from '../components/Logo';

const ERROR_MESSAGES: Readonly<Record<string, string>> = {
  twitch_not_configured: 'Вход через Twitch пока не настроен. Администратору нужно заполнить TWITCH_CLIENT_ID и TWITCH_CLIENT_SECRET.',
  access_denied: 'Доступ не был разрешён на стороне Twitch. Попробуй войти снова.',
  state_expired:
    'Время на подтверждение входа истекло. Начни авторизацию заново.',
  state_mismatch:
    'Не удалось подтвердить подлинность запроса. Начни вход заново в этом же браузере.',
  twitch_unavailable:
    'Twitch не ответил на запрос. Проверь соединение и повтори попытку.',
};

export function LoginPage() {
  const [params] = useSearchParams();
  const { login } = useAuth();

  const returnTo = params.get('returnTo') ?? '/dashboard';
  const errorCode = params.get('error');
  const errorMessage = errorCode
    ? (ERROR_MESSAGES[errorCode] ?? 'Вход не завершён. Попробуй ещё раз.')
    : null;

  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex items-center justify-between px-5 py-5 lg:px-10">
        <Link to="/" aria-label="GPS Tracker — на главную">
          <Logo />
        </Link>

        <Link to="/" className="btn-ghost">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          На главную
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-5 py-10">
        <div className="panel w-full max-w-md p-7">
          <p className="font-mono text-label uppercase tracking-[0.18em] text-muted">
            Вход в сервис
          </p>

          <h1 className="mt-3 text-3xl font-extrabold leading-tight tracking-tight text-ink">
            Подключи Twitch
            <br />
            и создай свой маршрут
          </h1>

          <p className="mt-4 text-sm leading-relaxed text-muted">
            Twitch нужен только для идентификации аккаунта: имя, логин и аватар.
            Сервис не получает доступ к трансляции, чату и подписчикам и не
            запрашивает дополнительных разрешений.
          </p>

          {errorMessage ? (
            <div
              className="mt-5 flex items-start gap-2.5 rounded-card border border-amber-400/35 bg-amber-400/10 p-3.5"
              role="alert"
            >
              <AlertTriangle
                className="mt-0.5 h-4 w-4 shrink-0 text-amber-300"
                aria-hidden
              />
              <p className="text-sm leading-relaxed text-amber-200">{errorMessage}</p>
            </div>
          ) : null}

          <button
            type="button"
            className="btn-violet mt-6 w-full py-3"
            onClick={() => login(returnTo)}
          >
            {/* Логотип Twitch как инлайновый SVG: отдельный запрос за иконкой
                на странице входа — лишняя задержка. */}
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
              <path d="M4.3 3 3 6.4v13.1h4.4V22h3.6l2.5-2.5h3.6L22 15V3H4.3Zm1.5 1.5h14.7v9.8l-2.9 2.9h-4.4L10.7 20v-2.8H5.8V4.5Zm4.4 3v5.1h1.5V7.5h-1.5Zm4.4 0v5.1h1.5V7.5h-1.5Z" />
            </svg>
            Войти через Twitch
          </button>

          <div className="mt-6 flex items-start gap-2.5 border-t border-edge pt-5">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-lime" aria-hidden />
            <p className="text-xs leading-relaxed text-muted">
              Сессия хранится в защищённой cookie на стороне сервера. Токены Twitch
              не передаются в браузер и не попадают в публичные ссылки на виджет.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
